import {
  StarIcon as StarIconOutline,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import React, { useState, useEffect, useRef } from 'react';

import ConfirmableButton from '../../../components/ConfirmableButton';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { useDeleteChat } from '../hooks/useDeleteChat';
import { useRenameChat } from '../hooks/useRenameChat';
import { useStarChat } from '../hooks/useStarChat';
import { Chat } from '../types';

interface ChatListItemProps {
  chat: Chat;
  onSelect: (chatId: string) => void;
}

const ChatListItem: React.FC<ChatListItemProps> = ({ chat, onSelect }) => {
  const { starChat } = useStarChat();
  const { renameChat } = useRenameChat();
  const { deleteChat, isDeleting } = useDeleteChat();
  const [isEditing, setIsEditing] = useState(false);
  const [editingTitle, setEditingTitle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStarClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await starChat({ chatId: chat.id, starred: !chat.starred });
  };

  const handleRenameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditingTitle(chat.title);
  };

  const handleRenameSubmit = async () => {
    if (editingTitle.trim() && editingTitle.trim() !== chat.title) {
      await renameChat({ chatId: chat.id, title: editingTitle.trim() });
    }
    setIsEditing(false);
    setEditingTitle('');
  };

  const handleRenameCancel = () => {
    setIsEditing(false);
    setEditingTitle('');
  };

  return (
    <div className='group relative'>
      {/* Delete Button - Top Right Corner */}
      <div className='absolute -top-2 -right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity'>
        {isDeleting ? (
          <LoadingSpinner size='sm' />
        ) : (
          <ConfirmableButton
            onConfirm={() => deleteChat(chat.id)}
            buttonClassName='p-1 bg-white rounded-full border border-blue-300 text-blue-500 hover:text-red-500 transition-colors'
            tooltip='Delete conversation'
          >
            <TrashIcon className='w-4 h-4' />
          </ConfirmableButton>
        )}
      </div>

      <button
        className='w-full p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer transition-all text-left'
        onClick={() => !isEditing && onSelect(chat.id)}
        onKeyDown={e => {
          if (isEditing && e.key === ' ') {
            e.preventDefault();
          }
        }}
        aria-label={`Open conversation: ${chat.title}`}
      >
        <div className='flex items-center justify-between'>
          <div className='flex items-center flex-1 min-w-0 gap-2'>
            <button
              onClick={handleStarClick}
              className='flex-shrink-0 hover:scale-110 transition-transform'
              aria-label={
                chat.starred ? 'Unstar conversation' : 'Star conversation'
              }
            >
              {chat.starred ? (
                <StarIconSolid className='w-4 h-4 text-yellow-500' />
              ) : (
                <StarIconOutline className='w-4 h-4 text-gray-400 hover:text-yellow-500' />
              )}
            </button>
            {isEditing ? (
              <input
                ref={inputRef}
                type='text'
                value={editingTitle}
                onChange={e => setEditingTitle(e.target.value)}
                onBlur={handleRenameSubmit}
                onKeyDown={e => {
                  e.stopPropagation();
                  if (e.key === 'Enter') {
                    handleRenameSubmit();
                  } else if (e.key === 'Escape') {
                    handleRenameCancel();
                  }
                }}
                className='flex-1 px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                onClick={e => e.stopPropagation()}
              />
            ) : (
              <div className='flex items-center flex-1 min-w-0 gap-2'>
                <h3 className='font-medium text-gray-900 truncate'>
                  {chat.title}
                </h3>
                <button
                  onClick={handleRenameClick}
                  className='flex-shrink-0 text-gray-400 hover:text-blue-500 hover:scale-110 transition-all opacity-0 group-hover:opacity-100'
                  aria-label='Rename conversation'
                >
                  <PencilIcon className='w-4 h-4' />
                </button>
              </div>
            )}
          </div>
          <p className='text-sm text-gray-500 ml-2 flex-shrink-0'>
            {new Date(chat.updatedAt).toLocaleDateString()}
          </p>
        </div>
      </button>
    </div>
  );
};

export default ChatListItem;
